import { db } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const COMMUNITY_NAME_REGEX = /^[A-Za-z0-9_]{3,30}$/;

const normalizeCommunityName = (name: string): string => name.trim();

const isValidCommunityName = (name: string): boolean =>
    COMMUNITY_NAME_REGEX.test(name);

/**
 * Checks if a community name is available by querying the users collection.
 * This avoids permission issues with a separate index collection.
 */
export const checkCommunityNameAvailability = async (name: string, requestingUserId?: string): Promise<boolean> => {
    const checkName = normalizeCommunityName(name);
    if (!isValidCommunityName(checkName)) return false;

    try {
        const q = query(collection(db, 'users'), where('communityName', '==', checkName));
        const snapshot = await getDocs(q);

        if (snapshot.empty) return true;

        let isOwnedByRequester = false;
        snapshot.forEach(doc => {
            if (doc.id === requestingUserId) {
                isOwnedByRequester = true;
            }
        });

        return isOwnedByRequester;
    } catch (error) {
        console.error("Error checking name availability:", error);
        return false;
    }
};

/**
 * Performs a final pre-save reservation check for a community name.
 * The caller is responsible for persisting the profile update after this returns true.
 */
export const claimCommunityName = async (userId: string, newName: string, oldName?: string): Promise<boolean> => {
    const normalizedNewName = normalizeCommunityName(newName);
    const normalizedOldName = oldName ? normalizeCommunityName(oldName) : '';

    if (!userId || !isValidCommunityName(normalizedNewName)) {
        return false;
    }

    if (normalizedOldName && normalizedOldName === normalizedNewName) {
        return true;
    }

    return checkCommunityNameAvailability(normalizedNewName, userId);
};
